// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Orcamentos.tsx
//  Página de listagem de Orçamentos
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, FileText, Clock, CheckCircle2, XCircle,
  Eye, Pencil, ChevronLeft, ChevronRight, AlertCircle, ShoppingCart,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { useOrcamento } from '@/features/operacional/hooks/useOrcamento';
import { calcularStatusOrcamento } from '@/features/operacional/types';
import type { OrcamentoResumo, StatusOrcamento } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusOrcamento, { label: string; className: string; icon: typeof Clock }> = {
  aberto:    { label: 'Aberto',    className: 'bg-blue-500/10 text-blue-500',     icon: Clock        },
  aprovado:  { label: 'Aprovado',  className: 'bg-green-500/10 text-green-500',   icon: CheckCircle2 },
  expirado:  { label: 'Expirado',  className: 'bg-orange-500/10 text-orange-500', icon: AlertCircle  },
  devolvido: { label: 'Devolvido', className: 'bg-gray-500/10 text-gray-500',     icon: XCircle      },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

// ── Componente ────────────────────────────────────────────────────────────

const Orcamentos = () => {
  const navigate = useNavigate();

  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias, mais recentes primeiro: com o tempo a lista inteira fica grande demais.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'cliente' | 'validade' | 'valor'>('emissao', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const { orcamentos, total, totalPaginas, resumo, isLoading, isFetching,
          deleteOrcamento, converterEmDav, isConvertendo } =
    useOrcamento({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  // ── KPIs (do servidor: busca + período inteiros, não só a página) ──────

  const totalAbertos      = resumo?.abertos      ?? 0;
  const totalAprovados    = resumo?.aprovados    ?? 0;
  const totalExpirados    = resumo?.expirados    ?? 0;
  const valorTotalAbertos = resumo?.valorAbertos ?? 0;

  // ── Paginação ──────────────────────────────────────────────────────────

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Orçamentos</h1>
          <p className="text-muted-foreground mt-1">Gerencie os orçamentos da empresa</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/orcamentos/novo')}>
          <Plus className="h-4 w-4" />
          Novo Orçamento
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Em Aberto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalAbertos}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {valorTotalAbertos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Aprovados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalAprovados}</div>
            <p className="text-xs text-muted-foreground mt-1">Convertidos em pedido</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />Expirados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{totalExpirados}</div>
            <p className="text-xs text-muted-foreground mt-1">Validade vencida</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />Total Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">orçamentos no filtro</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Orçamentos</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº, cliente ou CPF/CNPJ..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="aprovado">Aprovado</SelectItem>
                  <SelectItem value="expirado">Expirado</SelectItem>
                  <SelectItem value="devolvido">Devolvido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº Orç.</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="cliente" ordenacao={ordenacao} decrescentePadrao={false}>Cliente</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="validade" ordenacao={ordenacao}>Validade</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Total Líquido</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : orcamentos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo !== 'todos'
                        ? 'Nenhum orçamento neste período — amplie o período para ver os anteriores'
                        : 'Nenhum orçamento encontrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  orcamentos.map((orc: OrcamentoResumo) => {
                    const status = calcularStatusOrcamento(orc.isBaixada, orc.isDevolvido, orc.dtValidade);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    return (
                      <TableRow key={orc.id}>
                        <TableCell className="font-medium">#{orc.numeroOrcamento}</TableCell>
                        <TableCell>
                          {orc.nomeRazao || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {new Date(orc.dtEmissao).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          {new Date(orc.dtValidade).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {(orc.totalLiquido ?? 0).toLocaleString('pt-BR', {
                            style: 'currency', currency: 'BRL',
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {cfg.label}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {!orc.isBaixada && !orc.isDevolvido && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/orcamentos/${orc.id}`)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              title="Ver detalhes"
                              onClick={() => navigate(`/operacional/orcamentos/${orc.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!orc.isBaixada && !orc.isDevolvido && (
                              <Button
                                size="sm" variant="ghost"
                                title="Converter em DAV (Pedido de Venda)"
                                disabled={isConvertendo}
                                onClick={async () => {
                                  const dav = await converterEmDav(orc.id);
                                  navigate(`/operacional/dav/${dav.id}`);
                                }}
                              >
                                <ShoppingCart className="h-4 w-4 text-primary" />
                              </Button>
                            )}
                            {!orc.isBaixada && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setDeletingId(orc.id)}
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Rodapé de paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && (
                <span className="ml-2 text-xs">(atualizando...)</span>
              )}
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  Itens por página:
                </span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(1)}
                  disabled={pagina === 1}
                >
                  «
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p - 1)}
                  disabled={pagina === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button
                      key={page}
                      variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs"
                      onClick={() => setPagina(page)}
                    >
                      {page}
                    </Button>
                  );
                })}

                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p + 1)}
                  disabled={pagina >= totalPaginas}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(totalPaginas)}
                  disabled={pagina >= totalPaginas}
                >
                  »
                </Button>
              </div>

              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Página {pagina} de {totalPaginas}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => {
          if (deletingId) {
            deleteOrcamento(deletingId);
            setDeletingId(null);
          }
        }}
        title="Excluir Orçamento"
        description="Tem certeza que deseja excluir este orçamento? Orçamentos já aprovados não podem ser excluídos."
      />
    </div>
  );
};

export default Orcamentos;
